// ResellerBalanceModal.tsx
import React, { useEffect, useState } from 'react';
import {
    Modal,
    Box,
    Typography,
    Button,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Tooltip,
} from '@mui/material';
import axios from 'axios';
import { API_PATH } from '../../../App';
import { getAxiosConfig } from '../../../helpers/AuthHelper';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';

interface ResellerBalance {
    resellerId: number;
    resellerName: string;
    coinType: string;
    currentBalance: number;
}

const modalStyle = {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    maxWidth: 800,
    maxHeight: '80vh',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: 2,
    p: 3,
    overflow: 'auto',
};

interface ResellerBalanceModalProps {
    open: boolean;
    onClose: () => void;
}

const ResellerBalanceModal: React.FC<ResellerBalanceModalProps> = ({ open, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [balances, setBalances] = useState<ResellerBalance[]>([]);
    const [downloading, setDownloading] = useState(false);

    const fetchResellerBalances = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_PATH}/customer_ledger/reseller_balance`, getAxiosConfig());
            setBalances(response.data);
        } catch (err) {
            console.error('Failed to fetch reseller balances:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            fetchResellerBalances();
        }
    }, [open]);

    const downloadBalances = () => {
        setDownloading(true);
        try {
            // Create CSV content
            const headers = ['Reseller ID', 'Reseller Name', 'Coin Type', 'Current Balance'];
            
            const csvRows = balances.map(balance => [
                balance.resellerId,
                `"${balance.resellerName}"`,
                balance.coinType,
                balance.currentBalance.toFixed(10)
            ]);

            const csvContent = [
                headers.join(','),
                ...csvRows.map(row => row.join(','))
            ].join('\n');

            // Create and trigger download
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            
            const dateStr = new Date().toISOString().split('T')[0];
            const filename = `reseller_balances_${dateStr}.csv`;
            
            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error downloading balances:', error);
        } finally {
            setDownloading(false);
        }
    };

    const calculateTotalBalance = () => {
        return balances.reduce((total, balance) => total + balance.currentBalance, 0);
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="reseller-balance-modal-title"
            aria-describedby="reseller-balance-modal-description"
        >
            <Box sx={modalStyle}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography
                        id="reseller-balance-modal-title"
                        variant="h6"
                        component="h2"
                        fontWeight={600}
                    >
                        Reseller Balances
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Tooltip title="Download CSV">
                            <IconButton 
                                onClick={downloadBalances} 
                                disabled={downloading || balances.length === 0}
                                size="small"
                            >
                                {downloading ? <CircularProgress size={24} /> : <DownloadIcon />}
                            </IconButton>
                        </Tooltip>
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : balances.length === 0 ? (
                    <Typography color="text.secondary" textAlign="center" py={4}>
                        No reseller balances found
                    </Typography>
                ) : (
                    <>
                        <TableContainer component={Paper} sx={{ mb: 3 }}>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: 'primary.light' }}>
                                        <TableCell sx={{ fontWeight: 600 }}>Reseller ID</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Reseller Name</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Coin Type</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }} align="right">Current Balance</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {balances.map((balance) => (
                                        <TableRow key={balance.resellerId} hover>
                                            <TableCell>{balance.resellerId}</TableCell>
                                            <TableCell>{balance.resellerName}</TableCell>
                                            <TableCell>{balance.coinType}</TableCell>
                                            <TableCell align="right">
                                                {balance.currentBalance.toFixed(10)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'flex-end',
                            p: 2,
                            backgroundColor: 'grey.50',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'grey.300'
                        }}>
                            <Typography variant="h6" fontWeight={600}>
                                Total Balance: {calculateTotalBalance().toFixed(10)}
                            </Typography>
                        </Box>
                    </>
                )}
            </Box>
        </Modal>
    );
};

export default ResellerBalanceModal;